"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import FormInput from "../../../../components/FormInput";
import { computeAvailableTimesForDate } from "../utils/followUpSlots";
import scheduleStyles from "../../Booking/css/ScheduleConsult.module.css";

const isoToDmy = (iso) => {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  const [, y, mm, dd] = m;
  return `${dd}-${mm}-${y}`;
};

const dmyToIso = (dmy) => {
  const raw = String(dmy || "").trim();
  const m = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!m) return "";
  const [, dd, mm, yyyy] = m;
  return `${yyyy}-${mm}-${dd}`;
};

const toLocalISODate = (val) => {
  if (!val) return "";
  const d = val instanceof Date ? val : new Date(val);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const todayLocalISO = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export default function FollowUpDateTimePicker({ value, onChange, getAppointments }) {
  const currentTodayIso = useMemo(() => todayLocalISO(), []);
  const [availableTimes, setAvailableTimes] = useState([]);
  const nativeDateRef = useRef(null);

  const selectedIso = useMemo(() => dmyToIso(value?.followUpDateText), [value?.followUpDateText]);

  useEffect(() => {
    if (!value?.followUpDate || value?.followUpDateText) return;
    const d = new Date(value.followUpDate);
    if (Number.isNaN(d.getTime())) return;
    const iso = toLocalISODate(d);
    onChange({
      ...value,
      followUpDateText: iso ? isoToDmy(iso) : "",
      followUpTime: value?.followUpTime || `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
    });
  }, [onChange, value, value?.followUpDate, value?.followUpDateText]);

  useEffect(() => {
    const run = async () => {
      const isoDate = selectedIso;
      if (!isoDate) {
        setAvailableTimes([]);
        if (value?.followUpTime) onChange({ ...value, followUpTime: "" });
        return;
      }

      if (isoDate < currentTodayIso) {
        setAvailableTimes([]);
        onChange({ ...value, followUpTime: "", followUpDate: null });
        return;
      }

      let appts = [];
      try {
        appts = typeof getAppointments === "function" ? await getAppointments() : [];
      } catch {
        appts = [];
      }

      const slots = computeAvailableTimesForDate({
        isoDate,
        todayLocalISO: currentTodayIso,
        currentTime: new Date(),
        existingAppointments: appts,
        slotMinutes: 30,
      });

      setAvailableTimes(slots);

      const nextTime = slots.includes(value?.followUpTime) ? value.followUpTime : (slots[0] || "");
      if (nextTime !== value?.followUpTime) {
        onChange({ ...value, followUpTime: nextTime });
      }
    };

    run();
  }, [currentTodayIso, getAppointments, onChange, selectedIso, value]);

  const handleDateTextChange = (e) => {
    const nextText = e?.target?.value;
    const nextIso = dmyToIso(nextText);

    const next = { ...value, followUpDateText: nextText };

    if (!nextIso || nextIso < currentTodayIso) {
      next.followUpDate = null;
      next.followUpTime = "";
      onChange(next);
      return;
    }

    const time = value?.followUpTime || "09:00";
    const dt = new Date(`${nextIso}T${time}:00`);
    next.followUpDate = Number.isNaN(dt.getTime()) ? null : dt;
    onChange(next);
  };

  const openNativeCalendar = () => {
    const el = nativeDateRef.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === "function") {
        el.showPicker();
        return;
      }
    } catch {}
    try {
      el.click();
    } catch {}
  };

  const handleNativeDateChange = (e) => {
    const iso = e?.target?.value;
    if (!iso) return;

    const nextText = isoToDmy(iso);
    const time = value?.followUpTime || "09:00";
    const dt = new Date(`${iso}T${time}:00`);

    onChange({
      ...value,
      followUpDateText: nextText,
      followUpDate: Number.isNaN(dt.getTime()) ? null : dt,
    });
  };

  const handleTimeChange = (nextTime) => {
    const next = { ...value, followUpTime: nextTime };
    const nextIso = dmyToIso(value?.followUpDateText);

    if (nextIso && nextTime) {
      const dt = new Date(`${nextIso}T${nextTime}:00`);
      if (!Number.isNaN(dt.getTime())) next.followUpDate = dt;
    }

    onChange(next);
  };

  const isPastDate = selectedIso && selectedIso < currentTodayIso;
  const selectedTime = value?.followUpTime || "";
  const selectedHour = selectedTime.split(":")[0] || "";
  const selectedMinute = selectedTime.split(":")[1] || "00";

  return (
    <>
      <input
        ref={nativeDateRef}
        type="date"
        value={selectedIso || ""}
        onChange={handleNativeDateChange}
        min={currentTodayIso}
        style={{ position: "absolute", opacity: 0, pointerEvents: "none", width: 0, height: 0 }}
        tabIndex={-1}
        aria-hidden="true"
      />
      <FormInput
        label=""
        type="text"
        name="followUpDateText"
        value={value?.followUpDateText || ""}
        onChange={handleDateTextChange}
        onClick={openNativeCalendar}
        onFocus={openNativeCalendar}
        placeholder="DD-MM-YYYY"
        formatAsDate
      />

      {availableTimes.length > 0 ? (
        <div className={scheduleStyles.compactTimePicker}>
          <div className={scheduleStyles.timeSelector}>
            <select
              className={scheduleStyles.hourSelect}
              value={selectedHour}
              onChange={(e) => {
                const newHour = e.target.value;
                const availableWithHour = availableTimes.filter((t) => t.split(":")[0] === newHour);
                if (availableWithHour.length > 0) {
                  handleTimeChange(availableWithHour[0]);
                }
              }}
              disabled={!selectedIso || isPastDate}
            >
              {[...new Set(availableTimes.map((t) => parseInt(t.split(":")[0], 10)))]
                .sort((a, b) => a - b)
                .map((hour) => (
                  <option key={hour} value={hour.toString().padStart(2, "0")}
                  >
                    {hour.toString().padStart(2, "0")}
                  </option>
                ))}
            </select>

            <span className={scheduleStyles.colon}>:</span>

            <select
              className={scheduleStyles.minuteSelect}
              value={selectedMinute}
              onChange={(e) => {
                const newMinute = e.target.value;
                const currentHour = selectedHour || (availableTimes[0]?.split(":")[0] || "09");
                const newTime = `${currentHour}:${newMinute}`;
                if (availableTimes.includes(newTime)) {
                  handleTimeChange(newTime);
                }
              }}
              disabled={!selectedIso || isPastDate || !selectedHour}
            >
              {[...new Set(
                availableTimes
                  .filter((t) => t.split(":")[0] === (selectedHour || availableTimes[0]?.split(":")[0]))
                  .map((t) => t.split(":")[1])
              )]
                .sort()
                .map((minute) => (
                  <option key={minute} value={minute}>
                    {minute}
                  </option>
                ))}
            </select>
          </div>
        </div>
      ) : (
        <div className={scheduleStyles.noTimes}>
          <span>No times available</span>
        </div>
      )}

    </>
  );
}

FollowUpDateTimePicker.propTypes = {
  value: PropTypes.shape({
    followUpDate: PropTypes.any,
    followUpDateText: PropTypes.string,
    followUpTime: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  getAppointments: PropTypes.func,
};
